import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { ILicence, IMetadataArcs, IVessel } from "interfaces";
import React from "react";
import { formatMoney } from "components/metadata/formatMoney";

export const MetadataArcsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IMetadataArcs>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<IMetadataArcs>({
      mapData: (item) => {
        return {
          "UKHO Licence ID": item?.subscription?.licence?.ukhoLicenceId || "",
          "Product Name": item?.productName || "",
          "Product Group": item?.productGroup || "",
          "Currency": item?.currency || "",
          "Subscription Type": item?.subscriptionType || "",
          "Payment Frequency": item?.paymentFrequency || "",
          "Discount Percentage": item?.discountPercentage || "",
          "Annual Flat Fee Per Vessel":
            item?.annualFlatFeePerVessel == null
              ? ""
              : formatMoney(null, item.annualFlatFeePerVessel),
          "Activation Status": item?.activationStatus || "",
          "Licence Type": item?.licenceType || "",
          "Expiry Date": item?.expiryDate || "",
          "Master Permit": item?.masterPermit || "",
          "Master Permit Pin": item?.masterPermitPin || "",
          "Backup Permit": item?.backupPermit || "",
          "Backup Permit Pin": item?.backupPermitPin || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "UKHO Licence ID",
          "Product Name",
          "Product Group",
          "Currency",
          "Subscription Type",
          "Payment Frequency",
          "Discount Percentage",
          "Annual Flat Fee Per Vessel",
          "Activation Status",
          "Licence Type",
          "Expiry Date",
          "Master Permit",
          "Master Permit Pin",
          "Backup Permit",
          "Backup Permit Pin",
        ],
      },
    });

  const { selectProps: licenceSelectProps } = useSelect<ILicence>({
    resource: "licence",
    optionLabel: "ukhoLicenceId",
    optionValue: "ukhoLicenceId",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IMetadataArcs> = [
    {
      title: "UKHO Licence",
      dataIndex: ["subscription", "licence", "ukhoLicenceId"],
      key: "subscription.licence.ukhoLicenceId",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Licence"
            {...licenceSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "IMO Number",
      dataIndex: ["subscription", "licence", "vessel", "imoNumber"],
      key: "subscription.licence.vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO Number"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["subscription", "licence", "vessel", "name"],
      key: "subscription.licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 140,
      className: "wrap-text",
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("expiryDate", sorters),
      sorter: true,
    },
    {
      title: "Master Permit",
      dataIndex: "masterPermit",
      key: "masterPermit",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("masterPermit", sorters),
      sorter: true,
    },
    {
      title: "Backup Permit",
      dataIndex: "backupPermit",
      key: "backupPermit",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("backupPermit", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "ARCS Service",
        subTitle: "active ARCS services",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
